import { useDocumentVisibility, useIntervalFn } from '@vueuse/core'
import { watch, type Ref } from 'vue'

import { PendingMatch, Platform } from '@/enums/device'
import i18n from '@/libs/i18n'
import { useAppStore } from '@/stores/app'
import { usePendingStore } from '@/stores/pending'
import type { IPendingScreen } from '@/types/Device'

/*
 * PENDING SCREENS in the console (docs/API.md §5.3, §7): the polling that keeps the list fresh,
 * and the words a card uses.
 */

/** Naprave asks every 5 s (docs/API.md); the menu's badge and Pregled more calmly. */
export const PENDING_POLL_MS      = 5_000
export const PENDING_POLL_CALM_MS = 30_000

/**
 * Keeps `usePendingStore` fresh for a tenant: asked once at once (and whenever the tenant
 * changes or the person turns out to be an admin there), then every `everyMs` while the page
 * is visible, and again as soon as it comes back. Stops with the component.
 */
export const usePendingPoll = (tid: Ref<string>, everyMs: number) => {
    const appStore   = useAppStore()
    const store      = usePendingStore()
    const visibility = useDocumentVisibility()

    const refresh = () => { void store.load(tid.value) }

    const { pause, resume } = useIntervalFn(refresh, everyMs, { immediate: false })

    // The admin check waits for GET /me: until then nobody is an admin of anything.
    const isAdmin = () => appStore.isAdmin(tid.value)

    watch([tid, isAdmin], refresh, { immediate: true })

    watch(visibility, (now, before) => {
        if (now !== 'visible') {
            pause()

            return
        }

        if (before !== undefined) refresh()
        resume()
    }, { immediate: true })

    return { refresh }
}

/** "Amlogic X96 · android", or a plain "Neznana naprava". */
export const pendingDevice = (item: IPendingScreen): string =>
    [item.model, item.platform].filter(Boolean).join(' · ') || i18n.global.t('pending.unknown_device')

/** The icon of the kind of device the platform names. */
export const platformIcon = (platform: string): string => {
    switch (platform) {
        case Platform.Android: return 'i-lucide-tv-minimal'
        case Platform.Tizen:   return 'i-lucide-tv'
        case Platform.WebOS:   return 'i-lucide-tv'
        case Platform.Web:     return 'i-lucide-monitor'
        default:               return 'i-lucide-monitor-smartphone'
    }
}

/** "K7M QX" — the code as the screen shows it (docs/API.md §4.1 EnrolStarted). */
export const formatCode = (code: string): string => code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code

/**
 * A screen without a tenant that this console lists because it is on the network of one of
 * the tenant's admins: it is confirmed and turned away in this tenant, like the tenant's own.
 */
export const onSameNetwork = (item: IPendingScreen): boolean => item.matched_by === PendingMatch.Network
